import * as React from "react";
import { cn } from "@/lib/utils";

export interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  collapsed?: boolean;
}

export const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  ({ collapsed = false, className, children, ...rest }, ref) => (
    <aside
      ref={ref}
      data-collapsed={collapsed}
      className={cn(
        "flex h-full shrink-0 flex-col transition-[width] duration-200",
        "bg-bg-inverse text-text-inverse",
        "border-r border-r-[oklch(15%_0.01_80)]",
        collapsed ? "w-14" : "w-[232px]",
        className
      )}
      {...rest}
    >
      {children}
    </aside>
  )
);
Sidebar.displayName = "Sidebar";

export interface SidebarBrandProps {
  mark?: React.ReactNode;
  name: string;
  env?: string;
}

export function SidebarBrand({ mark, name, env }: SidebarBrandProps): React.ReactElement {
  return (
    <div className="flex items-center gap-2.5 border-b border-b-border-inverse px-5 pb-[2px] pt-[5px]">
      <span className="grid place-items-center rounded-[5px] from-brand-400 to-brand-600 text-[12px] font-bold tracking-[var(--tracking-tight)] text-white">
        {mark ?? "O"}
      </span>
      <span className="text-[14px] font-semibold tracking-tight">{name}</span>
      {env && (
        <span className="ml-auto border-border-inverse px-5 py-5 font-mono text-[10px] uppercase tracking-wider text-text-inverse-secondary">
          {env}
        </span>
      )}
    </div>
  );
}

export function SidebarGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <>
      <div className="px-4 pb-1.5 pt-3.5 text-[10px] font-semibold uppercase tracking-widest text-text-inverse-secondary">
        {label}
      </div>
      <div className="flex flex-col gap-px px-2 pb-1">{children}</div>
    </>
  );
}

export interface SidebarNavItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  icon?: React.ReactNode;
  label: string;
  badge?: React.ReactNode;
  badgeTone?: "neutral" | "danger";
}

export const SidebarNavItem = React.forwardRef<HTMLButtonElement, SidebarNavItemProps>(
  ({ active = false, icon, label, badge, badgeTone = "neutral", className, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      data-active={active}
      title={label}
      aria-label={label}
      className={cn(
        "flex items-center gap-2.5 rounded-[5px] px-2.5 py-[7px] text-left text-[13px]",
        "text-text-inverse-secondary",
        "transition-colors motion-safe:[transition-duration:120ms]",
        "hover:bg-bg-inverse-muted hover:text-text-inverse",
        active &&
          "bg-[oklch(50%_0.180_285_/_0.18)] text-white shadow-[inset_2px_0_0_var(--brand-400)]",
        "focus-visible:shadow-rd-focus focus-visible:outline-none",
        className
      )}
      {...rest}
    >
      {icon && <span className="grid h-4 w-4 place-items-center opacity-85">{icon}</span>}
      <span className="truncate">{label}</span>
      {badge !== undefined && badge !== null && (
        <span
          className={cn(
            "ml-auto rounded-[3px] px-1.5 py-px font-mono text-[10px]",
            badgeTone === "danger"
              ? "bg-[oklch(60%_0.190_25_/_0.22)] text-[oklch(85%_0.10_25)]"
              : "bg-bg-inverse-muted text-text-inverse"
          )}
        >
          {badge}
        </span>
      )}
    </button>
  )
);
SidebarNavItem.displayName = "SidebarNavItem";
